import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import BackToCockpit from "@/components/BackToCockpit";
import { ShieldCheck, Loader2, User, Plane, PhoneCall, Utensils, ArrowRight, Check } from "lucide-react";

const GENDERS = ["M", "F", "X"];
const MEALS = ["Standard", "Vegan", "Vegetarian", "Gluten-Free", "Kosher", "Halal", "Diabetic", "Low-Sodium"];

export default function RiderTSA() {
  const [f, setF] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/rider/tsa")
      .then((r) => setF(r.data || {}))
      .catch((e) => {
        if (e?.response?.status === 403) {
          toast.error("TSA profile is a rider perk — join first.");
          nav("/empty-leg");
        }
      })
      .finally(() => setLoading(false));
  }, [nav]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      const r = await api.put("/rider/tsa", f);
      setF(r.data.profile);
      const pct = r.data.profile.completion_pct;
      toast.success(pct >= 100 ? "TSA profile complete — you're clear to book." : `TSA profile ${pct}% complete · saved.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save profile.");
    } finally { setSaving(false); }
  };

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 text-cyan animate-spin" /></div>;
  }

  const pct = f.completion_pct || 0;

  return (
    <div className="max-w-4xl mx-auto px-6 pt-8 pb-24" data-testid="rider-tsa-page">
      <BackToCockpit />

      {/* Header */}
      <div className="mt-6 rounded-2xl border border-cyan/25 p-6 md:p-8 relative overflow-hidden"
        style={{ background: "linear-gradient(140deg, rgba(75,156,255,0.12) 0%, rgba(5,8,16,0.9) 60%)" }}>
        <div className="mono text-[10px] tracking-[0.24em] uppercase text-cyan/80">// SECURE FLIGHT · TSA PROFILE</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-2">Your passenger identity, once.</h1>
        <p className="text-slate-300 mt-2 max-w-2xl">
          Every part 135 / part 91 charter requires TSA Secure Flight data before takeoff. Complete this once and
          every empty-leg or paid charter reservation you make on PJP auto-populates the manifest.
        </p>

        <div className="mt-5">
          <div className="flex items-center justify-between text-[11px] mono text-white/60">
            <span>Profile completion</span><span>{pct}%</span>
          </div>
          <div className="h-2 mt-1 rounded-full bg-white/[0.06] overflow-hidden">
            <div className="h-full transition-all"
              style={{ width: `${pct}%`, background: pct >= 100 ? "#2ED573" : "#4B9CFF" }} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Badge on={f.tsa_ready} label="TSA Secure Flight" />
            <Badge on={f.intl_ready} label="International ready" />
            <Badge on={f.emergency_ready} label="Emergency contact" />
          </div>
        </div>
      </div>

      {/* Sections */}
      <Section icon={ShieldCheck} title="TSA Secure Flight" subtitle="Required by 49 CFR 1560 — must match your government-issued ID exactly.">
        <Grid>
          <Text label="Legal first name" v={f.legal_first_name} onChange={(v) => set("legal_first_name", v)} tid="tsa-first" />
          <Text label="Legal middle name" v={f.legal_middle_name} onChange={(v) => set("legal_middle_name", v)} tid="tsa-middle" />
          <Text label="Legal last name" v={f.legal_last_name} onChange={(v) => set("legal_last_name", v)} tid="tsa-last" />
          <Text label="Date of birth" type="date" v={f.date_of_birth} onChange={(v) => set("date_of_birth", v)} tid="tsa-dob" />
          <Radio label="Gender" opts={GENDERS} v={f.gender} onChange={(v) => set("gender", v)} tid="tsa-gender" />
          <Text label="Known Traveler # (KTN)" v={f.ktn} onChange={(v) => set("ktn", v)} tid="tsa-ktn" hint="Global Entry / PreCheck / NEXUS / SENTRI" />
          <Text label="Redress # (optional)" v={f.redress_number} onChange={(v) => set("redress_number", v)} tid="tsa-redress" hint="DHS TRIP redress control number" />
        </Grid>
      </Section>

      <Section icon={Plane} title="Passport" subtitle="Required for international empty-leg reservations only.">
        <Grid>
          <Text label="Passport number" v={f.passport_number} onChange={(v) => set("passport_number", v)} tid="tsa-passport-num" />
          <Text label="Issuing country (ISO code)" v={f.passport_country} onChange={(v) => set("passport_country", (v || "").toUpperCase())} tid="tsa-passport-country" hint="e.g. USA, GBR" />
          <Text label="Expiration date" type="date" v={f.passport_expiration} onChange={(v) => set("passport_expiration", v)} tid="tsa-passport-exp" />
        </Grid>
      </Section>

      <Section icon={PhoneCall} title="Emergency contact" subtitle="Who should we call if something happens en-route?">
        <Grid>
          <Text label="Full name" v={f.emergency_contact_name} onChange={(v) => set("emergency_contact_name", v)} tid="tsa-em-name" />
          <Text label="Phone (incl. country code)" v={f.emergency_contact_phone} onChange={(v) => set("emergency_contact_phone", v)} tid="tsa-em-phone" />
          <Text label="Relation" v={f.emergency_contact_relation} onChange={(v) => set("emergency_contact_relation", v)} tid="tsa-em-relation" hint="Spouse / parent / sibling / assistant" />
        </Grid>
      </Section>

      <Section icon={Utensils} title="Passenger preferences" subtitle="Applied automatically to every booking so operators can prep in advance.">
        <Grid>
          <Radio label="Meal preference" opts={MEALS} v={f.meal_preference} onChange={(v) => set("meal_preference", v)} tid="tsa-meal" />
          <Text label="Allergies / dietary restrictions" v={f.dietary_restrictions} onChange={(v) => set("dietary_restrictions", v)} tid="tsa-diet" />
          <Text label="Typical +1 count" type="number" v={f.default_plus_ones} onChange={(v) => set("default_plus_ones", parseInt(v || "0", 10))} tid="tsa-plus" hint="How many companions you usually bring" />
          <Toggle label="I sometimes travel with a pet" v={!!f.pets_traveling} onChange={(v) => set("pets_traveling", v)} tid="tsa-pets" />
        </Grid>
      </Section>

      {/* Actions */}
      <div className="mt-8 flex flex-wrap gap-3 justify-end">
        <Link to="/rider/dashboard" className="btn-hud">← Back to dashboard</Link>
        <button type="button" onClick={submit} disabled={saving} data-testid="tsa-save" className="btn-hud btn-hud-solid">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="h-3.5 w-3.5" /> SAVE PROFILE</>}
        </button>
      </div>
    </div>
  );
}

// ── UI primitives ────────────────────────────────────────────────────────────
function Badge({ on, label }) {
  return (
    <span className={`mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full border flex items-center gap-1 ${
      on ? "border-emerald-400/50 bg-emerald-400/10 text-emerald-300" : "border-white/15 text-white/50"
    }`}>
      {on ? <Check className="h-3 w-3" /> : <span className="h-2 w-2 rounded-full border border-white/40" />}
      {label}
    </span>
  );
}

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="mt-8 rounded-2xl border border-white/10 bg-black/30 p-5 md:p-7">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg border border-cyan/40 bg-cyan/10 text-cyan flex-shrink-0"><Icon className="h-4 w-4" /></div>
        <div>
          <div className="font-display text-lg font-semibold text-white">{title}</div>
          <div className="text-[12px] text-white/60 mt-0.5">{subtitle}</div>
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function Grid({ children }) { return <div className="grid md:grid-cols-2 gap-4">{children}</div>; }

function Text({ label, v, onChange, type = "text", tid, hint }) {
  return (
    <div>
      <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">{label}</div>
      <input
        type={type}
        value={v || ""}
        onChange={(e) => onChange(e.target.value)}
        className="input w-full"
        data-testid={tid}
      />
      {hint && <div className="text-[10.5px] text-white/40 mt-1">{hint}</div>}
    </div>
  );
}

function Radio({ label, opts, v, onChange, tid }) {
  return (
    <div>
      <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {opts.map((o) => (
          <button
            key={o}
            type="button"
            onClick={() => onChange(o)}
            data-testid={`${tid}-${o}`}
            className={`mono text-[10.5px] tracking-widest uppercase px-2.5 py-1 rounded-full border ${
              v === o ? "border-cyan/70 bg-cyan/15 text-cyan" : "border-white/15 text-white/60 hover:bg-white/5"
            }`}
          >{o}</button>
        ))}
      </div>
    </div>
  );
}

function Toggle({ label, v, onChange, tid }) {
  return (
    <div>
      <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">{label}</div>
      <button
        type="button"
        onClick={() => onChange(!v)}
        data-testid={tid}
        className={`mono text-[10.5px] tracking-widest uppercase px-3 py-1.5 rounded-full border ${
          v ? "border-cyan/70 bg-cyan/15 text-cyan" : "border-white/15 text-white/60"
        }`}
      >{v ? "Yes" : "No"}</button>
    </div>
  );
}
